import React from 'react';
import {
  calculateCompositePricing,
} from '../../utils/feeCategoryCompositePricing';
import {
  mergeTierCompositePricing,
  TierCommonSettings,
  TierLevelField,
} from '../../utils/tierCompositePricing';
import { FeeTierData } from '../../api/feeCategoriesApi';

interface TierLevelPricingFieldsProps {
  tier: FeeTierData;
  tierIndex: number;
  common: TierCommonSettings;
  vatPercentage: number;
  onChange: (index: number, field: TierLevelField, value: number | null) => void;
  formatNumberDisplay: (value: number | string) => string;
  parseNumberInput: (value: string) => string;
  t: (key: string) => string;
}

const TierLevelPricingFields: React.FC<TierLevelPricingFieldsProps> = ({
  tier,
  tierIndex,
  common,
  vatPercentage,
  onChange,
  formatNumberDisplay,
  parseNumberInput,
  t,
}) => {
  const breakdown = calculateCompositePricing(
    mergeTierCompositePricing(tier, common, vatPercentage),
  );

  const numberInput = (
    field: TierLevelField,
    value: number | undefined | null,
    placeholder?: string,
  ) => (
    <input
      type="text"
      value={formatNumberDisplay(value ?? '')}
      onChange={(e) => {
        const raw = parseNumberInput(e.target.value);
        onChange(tierIndex, field, raw === '' ? null : parseFloat(raw) || 0);
      }}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      placeholder={placeholder}
    />
  );

  return (
    <div className="mt-4 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <h5 className="text-sm font-semibold text-gray-800">
        {`${t('usageLevel')} ${tier.tierLevel}`}
      </h5>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('unitPriceLabel')}</label>
          {numberInput('basePrice', tier.basePrice, t('pricePlaceholder'))}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vatAmountLabel')}</label>
          {numberInput('vatAmount', tier.vatAmount ?? breakdown.vatAmount)}
          <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(breakdown.vatAmount)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('priceWithVatLabel')}</label>
          <input
            type="text"
            readOnly
            value={formatNumberDisplay(breakdown.priceWithVat)}
            className="w-full border border-gray-200 bg-gray-100 rounded-lg px-3 py-2 text-gray-700"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('totalUnitPriceAfterTax')}</label>
          {numberInput('totalUnitPrice', tier.totalUnitPrice ?? breakdown.totalUnitPrice)}
          <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(breakdown.totalUnitPrice)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-200 pt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {common.extraFee1Name?.trim() || t('extraFeeAmountLabel')} ({t('extraFeeGroup1Title')})
          </label>
          {numberInput('extraFee1Amount', tier.extraFee1Amount ?? breakdown.extraFee1Amount)}
          <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(breakdown.extraFee1Amount)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeVatAmountLabel')}</label>
          {numberInput('extraFee1VatAmount', tier.extraFee1VatAmount ?? breakdown.extraFee1VatAmount)}
          <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(breakdown.extraFee1VatAmount)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {common.extraFee2Name?.trim() || t('extraFeeAmountLabel')} ({t('extraFeeGroup2Title')})
          </label>
          {numberInput('extraFee2Amount', tier.extraFee2Amount ?? breakdown.extraFee2Amount)}
          <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(breakdown.extraFee2Amount)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('extraFeeVatAmountLabel')}</label>
          {numberInput('extraFee2VatAmount', tier.extraFee2VatAmount ?? breakdown.extraFee2VatAmount)}
          <p className="text-xs text-gray-500 mt-1">{t('calculatedValue')}: {formatNumberDisplay(breakdown.extraFee2VatAmount)}</p>
        </div>
      </div>
    </div>
  );
};

export default TierLevelPricingFields;
